/**
 * ReportBuilderScreen — Enhanced AI Copilot Chat
 * Sprint 1: mode selector, context-aware entry, rich mock responses, action chips
 */

import { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router';
import {
  ArrowLeft, Send, Sparkles, FileText, RefreshCw, Download,
  BookOpen, Zap, MessageCircle, Share2, Bookmark, Calendar,
  Users, ChevronDown, UserPlus, MapPin, History,
} from 'lucide-react';
import chatThreadService from '../services/chatThreadService';
import { notify } from '../utils/notifications';
import { useApp } from '../context/AppContext';
import {
  generateAIResponse,
  getAvailableAIModes,
  getAIModeSuggestions,
  filterActionChips,
  MODE_CONFIG,
  type AIMode,
  type AIContext,
  type ActionChip,
} from '../services/aiService';
import { MarkdownContent } from '../components/MarkdownContent';
import { ReportAIResponseButton } from '../components/ReportAIResponseButton';
import { useEventModules } from '../hooks/useEventModules';
import { useKeyboardVisible } from '../hooks/useKeyboardVisible';
import { AIAvailabilityNotice } from '../components/AIAvailabilityNotice';

// ─── Icon resolver (maps string → lucide component) ─────────────────────────

const ICON_MAP: Record<string, React.ReactNode> = {
  Download:      <Download className="w-3.5 h-3.5" />,
  Share2:        <Share2 className="w-3.5 h-3.5" />,
  FileText:      <FileText className="w-3.5 h-3.5" />,
  RefreshCw:     <RefreshCw className="w-3.5 h-3.5" />,
  MessageCircle: <MessageCircle className="w-3.5 h-3.5" />,
  BookOpen:      <BookOpen className="w-3.5 h-3.5" />,
  Bookmark:      <Bookmark className="w-3.5 h-3.5" />,
  Calendar:      <Calendar className="w-3.5 h-3.5" />,
  Users:         <Users className="w-3.5 h-3.5" />,
  Sparkles:      <Sparkles className="w-3.5 h-3.5" />,
  Zap:           <Zap className="w-3.5 h-3.5" />,
  UserPlus:      <UserPlus className="w-3.5 h-3.5" />,
  MapPin:        <MapPin className="w-3.5 h-3.5" />,
};

function ChipIcon({ name }: { name: string }) {
  return <>{ICON_MAP[name] ?? <Sparkles className="w-3.5 h-3.5" />}</>;
}

// ─── Mode icon component ─────────────────────────────────────────────────────

function ModeIcon({ mode, className }: { mode: AIMode; className?: string }) {
  const cls = className || 'w-4 h-4';
  switch (mode) {
    case 'report':        return <FileText className={cls} />;
    case 'paper-insight': return <BookOpen className={cls} />;
    case 'session-prep':  return <Zap className={cls} />;
    case 'networking':    return <Users className={cls} />;
    default:              return <Sparkles className={cls} />;
  }
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Message = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  chips?: ActionChip[];
  receiptId?: string;
  timestamp: Date;
};

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ReportBuilderScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { addNote, currentEventId, authSession, isAuthSessionCurrent } = useApp();
  const { isEnabled } = useEventModules();
  const availableModes = getAvailableAIModes(isEnabled);

  // History handed off from the mini modal (via router state)
  const handoffState = location.state as {
    messages?: { role: 'user' | 'assistant'; content: string; receiptId?: string }[];
    context?: AIContext;
    pendingMessage?: string;
    thread_id?: string;
    thread_summary?: string;
  } | null;

  // Derive initial mode and context — prefer handoff state, then URL params
  const requestedInitialMode = (handoffState?.context?.mode as AIMode) || (searchParams.get('mode') as AIMode) || 'general';
  const initialMode = availableModes.includes(requestedInitialMode) ? requestedInitialMode : 'general';
  const requestedContext: AIContext = handoffState?.context ?? {
    mode: initialMode,
    eventId: currentEventId ?? undefined,
    sessionId:    searchParams.get('sessionId') || undefined,
    sessionTitle: searchParams.get('sessionTitle') || undefined,
    sessionTime:  searchParams.get('sessionTime') || undefined,
    sessionTrack: searchParams.get('sessionTrack') || undefined,
    paperId:       searchParams.get('paperId') || undefined,
    paperTitle:    searchParams.get('paperTitle') || undefined,
    paperAuthors:  searchParams.get('paperAuthors') || undefined,
    paperVenue:    searchParams.get('paperVenue') || undefined,
    paperAbstract: searchParams.get('paperAbstract') || undefined,
    attendeeId:   searchParams.get('attendeeId') || undefined,
    attendeeName: searchParams.get('attendeeName') || undefined,
    attendeeTitle: searchParams.get('attendeeTitle') || undefined,
    attendeeCompany: searchParams.get('attendeeCompany') || undefined,
    attendeeInterests: searchParams.getAll('attendeeInterests').length > 0 ? searchParams.getAll('attendeeInterests') : undefined,
  };
  const initialContext: AIContext = { ...requestedContext, mode: initialMode };

  // Seed messages from handoff history, or start fresh with a greeting
  const seedMessages: Message[] = handoffState?.messages?.length
    ? handoffState.messages.map((m, i) => ({
        id: String(i),
        role: m.role,
        content: m.content,
        receiptId: m.receiptId,
        timestamp: new Date(),
      }))
    : [{ id: '0', role: 'assistant', content: MODE_CONFIG[initialMode].greeting, timestamp: new Date() }];

  const [mode, setMode] = useState<AIMode>(initialMode);
  const [context, setContext] = useState<AIContext>(initialContext);
  const [messages, setMessages] = useState<Message[]>(seedMessages);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [progressLabel, setProgressLabel] = useState('');
  const [hasResponse, setHasResponse] = useState(seedMessages.some(m => m.role === 'assistant' && m.id !== '0'));
  const [showModePicker, setShowModePicker] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const keyboardVisible = useKeyboardVisible();
  const pendingSentRef = useRef(false);
  const threadIdRef = useRef<string | null>(handoffState?.thread_id ?? null);
  const [loadingThread, setLoadingThread] = useState(!!handoffState?.thread_id && !handoffState?.messages?.length);
  const [threadSummary, setThreadSummary] = useState<string | null>(handoffState?.thread_summary ?? null);

  useEffect(() => {
    if (availableModes.includes(mode)) return;
    setMode('general');
    setContext((current) => ({ ...current, mode: 'general' }));
    setMessages([{ id: String(Date.now()), role: 'assistant', content: MODE_CONFIG.general.greeting, timestamp: new Date() }]);
    setShowModePicker(false);
  }, [availableModes.join('|'), mode]);

  // Has a context entity been passed in via query params?
  const hasContext =
    !!(context.sessionTitle || context.paperTitle || context.attendeeName);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
  }, [messages, isLoading, keyboardVisible]);

  // Load messages from DB when navigated to from ChatHistoryScreen
  useEffect(() => {
    if (!handoffState?.thread_id || handoffState?.messages?.length) return;
    const session = authSession;
    if (!session || !isAuthSessionCurrent(session)) {
      setLoadingThread(false);
      return;
    }
    let disposed = false;
    void (async () => {
      try {
        const records = await chatThreadService.getMessages(
          handoffState.thread_id!,
          { signal: session.signal },
        );
        if (disposed || !isAuthSessionCurrent(session)) return;
        const loaded: Message[] = records.map((r, i) => ({
          id: String(i),
          role: r.role as 'user' | 'assistant',
          content: r.content,
          receiptId: r.receipt_id ?? undefined,
          timestamp: new Date(r.created_at),
        }));
        if (loaded.length) setMessages(loaded);
      } catch {
        // Non-fatal: the composer remains usable without history.
      } finally {
        if (!disposed && isAuthSessionCurrent(session)) {
          setLoadingThread(false);
        }
      }
    })();
    return () => { disposed = true; };
  }, [
    authSession,
    handoffState?.messages?.length,
    handoffState?.thread_id,
    isAuthSessionCurrent,
  ]);

  // Switch mode — resets conversation with new greeting
  const switchMode = (newMode: AIMode) => {
    if (newMode === mode) { setShowModePicker(false); return; }
    const newContext: AIContext = { ...context, mode: newMode };
    setMode(newMode);
    setContext(newContext);
    setMessages([
      {
        id: Date.now().toString(),
        role: 'assistant',
        content: MODE_CONFIG[newMode].greeting,
        timestamp: new Date(),
      },
    ]);
    setHasResponse(false);
    setShowModePicker(false);
    inputRef.current?.focus();
  };

  const sendMessage = async (text?: string) => {
    const content = text || input.trim();
    const session = authSession;
    if (
      !content ||
      isLoading ||
      !session ||
      !isAuthSessionCurrent(session)
    ) return;

    setInput('');
    const userMsg: Message = {
      id: Date.now().toString(),
      role: 'user',
      content,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMsg]);
    setIsLoading(true);
    setProgressLabel('Thinking…');

    try {
      const rawHistory = messages.map(m => ({ role: m.role, content: m.content }));
      const history = rawHistory.slice(-20);

      // Stream the answer token-by-token. The assistant bubble is created lazily
      // on the first token; specialized first-message routes (which don't stream)
      // fall through to the append below.
      const aiId = (Date.now() + 1).toString();
      let streamed = '';
      let streaming = false;
      const response = await generateAIResponse(
        content,
        { ...context, threadId: threadIdRef.current ?? undefined },
        status => {
          if (isAuthSessionCurrent(session)) setProgressLabel(status);
        },
        token => {
          if (!isAuthSessionCurrent(session)) return;
          streamed += token;
          if (!streaming) {
            streaming = true;
            setMessages(prev => [...prev, { id: aiId, role: 'assistant', content: streamed, timestamp: new Date() }]);
          } else {
            setMessages(prev => prev.map(m => (m.id === aiId ? { ...m, content: streamed } : m)));
          }
        },
        history,
        threadSummary ?? undefined,
        session.signal,
      );
      if (!isAuthSessionCurrent(session)) return;

      // Finalize: replace streamed text with the authoritative full content and
      // attach action chips (also handles the non-streamed specialized routes).
      if (streaming) {
        setMessages(prev => prev.map(m => (m.id === aiId ? { ...m, content: response.content, chips: response.chips, receiptId: response.receiptId } : m)));
      } else {
        setMessages(prev => [...prev, { id: aiId, role: 'assistant', content: response.content, chips: response.chips, receiptId: response.receiptId, timestamp: new Date() }]);
      }
      setHasResponse(true);

      // ── Persist to DB ──────────────────────────────────────────────────────
      try {
        if (!threadIdRef.current) {
          if (!isAuthSessionCurrent(session)) return;
          const dateLabel = new Date().toLocaleDateString([], { month: 'short', day: 'numeric' });
          const ctxType = context.paperId ? 'paper' : context.sessionId ? 'session' : 'general';
          const ctxId = context.paperId ?? context.sessionId ?? undefined;
          const title = context.paperTitle
            ? `Paper: ${context.paperTitle.slice(0, 80)}`
            : context.sessionTitle
            ? `Session: ${context.sessionTitle.slice(0, 80)}`
            : `General · ${dateLabel}`;
          const thread = await chatThreadService.findOrCreate(
            {
              event_id: currentEventId ?? undefined,
              context_type: ctxType,
              context_id: ctxId,
              title,
            },
            { signal: session.signal },
          );
          if (!isAuthSessionCurrent(session)) return;
          threadIdRef.current = thread.id;
          setThreadSummary(thread.summary ?? null);
        }
        if (!isAuthSessionCurrent(session)) return;
        const threadId = threadIdRef.current;
        if (!threadId) return;
        const appendResult = await chatThreadService.appendMessages(
          threadId,
          [
            { role: 'user', content },
            { role: 'assistant', content: response.content, receipt_id: response.receiptId },
          ],
          { signal: session.signal },
        );
        if (!isAuthSessionCurrent(session)) return;
        setThreadSummary(appendResult.summary ?? null);
      } catch {
        // Non-fatal
      }
    } catch {
      if (isAuthSessionCurrent(session)) {
        notify.error('Could not generate a response', 'Please try again.');
      }
    } finally {
      if (isAuthSessionCurrent(session)) {
        setIsLoading(false);
        setProgressLabel('');
      }
    }
  };

  // Auto-send a question handed off from the mini modal while AI was mid-response
  useEffect(() => {
    const pending = handoffState?.pendingMessage;
    if (pending && !pendingSentRef.current) {
      pendingSentRef.current = true;
      sendMessage(pending);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChipAction = (chip: ActionChip) => {
    switch (chip.action) {
      case 'export':
        notify.success('Exporting report...', 'Saved to your device');
        break;
      case 'share':
        notify.success('Link copied to clipboard');
        break;
      case 'save-note': {
        if (!isEnabled('notes')) {
          notify.info('Notes are not available for this event.');
          break;
        }
        const lastAI = [...messages].reverse().find(m => m.role === 'assistant');
        if (lastAI) {
          const sourceMap: Record<string, 'ai-report' | 'ai-session-prep' | 'ai-paper-insight' | 'ai-networking' | 'ai-copilot'> = {
            report: 'ai-report',
            'session-prep': 'ai-session-prep',
            'paper-insight': 'ai-paper-insight',
            networking: 'ai-networking',
            general: 'ai-copilot',
          };
          const session = authSession;
          if (!session || !isAuthSessionCurrent(session)) break;
          const saved = addNote({
            title: `${modeConfig.label} — ${new Date().toLocaleDateString()}`,
            content: lastAI.content,
            source: sourceMap[mode] || 'ai-copilot',
            sourceLabel: modeConfig.label,
            sessionId: context.sessionId,
            sessionTitle: context.sessionTitle,
            paperId: context.paperId,
            paperTitle: context.paperTitle,
            attendeeId: context.attendeeId,
            attendeeName: context.attendeeName,
          }, session);
          if (saved) notify.success('Saved as note', 'Find it in My Notes');
        }
        break;
      }
      case 'save': {
        if (!isEnabled('notes')) {
          notify.info('Notes are not available for this event.');
          break;
        }
        const lastAI2 = [...messages].reverse().find(m => m.role === 'assistant');
        if (lastAI2) {
          const sourceMap2: Record<string, 'ai-report' | 'ai-session-prep' | 'ai-paper-insight' | 'ai-networking' | 'ai-copilot'> = {
            report: 'ai-report',
            'session-prep': 'ai-session-prep',
            'paper-insight': 'ai-paper-insight',
            networking: 'ai-networking',
            general: 'ai-copilot',
          };
          const session = authSession;
          if (!session || !isAuthSessionCurrent(session)) break;
          const saved = addNote({
            title: `${modeConfig.label} — ${new Date().toLocaleDateString()}`,
            content: lastAI2.content,
            source: sourceMap2[mode] || 'ai-copilot',
            sourceLabel: modeConfig.label,
            sessionId: context.sessionId,
            sessionTitle: context.sessionTitle,
            paperId: context.paperId,
            paperTitle: context.paperTitle,
            attendeeId: context.attendeeId,
            attendeeName: context.attendeeName,
          }, session);
          if (saved) notify.success('Saved to library');
        }
        break;
      }
      case 'refine':
        sendMessage('Please refine this with more detail and additional insights');
        break;
      case 'follow-up':
        inputRef.current?.focus();
        break;
      case 'more-questions':
        sendMessage('Generate 5 more sharp questions I could ask the speaker');
        break;
      case 'related':
        sendMessage('List the most relevant related papers or sessions I should explore');
        break;
      case 'take-notes':
        if (!isEnabled('notes')) break;
        notify.info('Opening notes...');
        navigate('/notes');
        break;
      case 'schedule':
        if (!isEnabled('agenda')) break;
        navigate('/agenda');
        break;
      case 'network':
      case 'view-matches':
        if (!isEnabled('attendees')) break;
        navigate('/explore/attendees');
        break;
      case 'send-intro':
        notify.success('Introduction message drafted', 'Saved to My Notes');
        break;
      case 'save-contact':
        notify.success('Contact saved', 'Added to your network');
        break;
      case 'more-starters':
        sendMessage('Generate 5 more creative conversation starters for this person');
        break;
      case 'find-similar':
        sendMessage('Find other attendees with similar profiles and interests');
        break;
      case 'network-map':
        navigate('/map');
        break;
      default:
        notify.info(chip.label);
    }
  };

  const handleDownload = () => notify.success('Report downloaded', 'Saved to your device');

  const modeConfig = MODE_CONFIG[mode];
  const MODES = availableModes;
  const modeSuggestions = getAIModeSuggestions(mode, isEnabled);

  return (
    <div className="size-full flex flex-col overflow-hidden" style={{ background: 'var(--app-bg)' }}>

      {/* ── Header ── */}
      <div
        className="app-screen-header flex items-center justify-between px-4 pt-12 pb-3 shrink-0 border-b"
        style={{ background: 'var(--app-bg)', borderColor: 'var(--app-border)' }}
      >
        <div className="flex items-center gap-1">
          <button
            onClick={() => navigate(-1)}
            className="size-10 rounded-full flex items-center justify-center active:scale-90 transition-transform"
            style={{ background: 'var(--app-surface)' }}
          >
            <ArrowLeft className="w-5 h-5" style={{ color: 'var(--app-text)' }} />
          </button>
          <button
            onClick={() => navigate('/chat-history')}
            className="size-10 rounded-full flex items-center justify-center active:scale-90 transition-transform"
            style={{ background: 'var(--app-surface)' }}
            title="Chat history"
          >
            <History className="w-4 h-4" style={{ color: 'var(--app-text-secondary, var(--app-text))' }} />
          </button>
        </div>

        {/* Mode selector trigger */}
        <button
          onClick={() => setShowModePicker(v => !v)}
          className="min-w-0 flex items-center gap-1.5 px-3 min-h-9 py-1 rounded-full border transition-colors active:scale-[0.97]"
          style={{ background: 'var(--app-surface)', borderColor: 'var(--app-accent-border)', color: 'var(--app-accent)' }}
        >
          <ModeIcon mode={mode} className="w-4 h-4" />
          <span className="text-xs">{modeConfig.label}</span>
          <ChevronDown className="w-3 h-3 opacity-60" />
        </button>

        {keyboardVisible ? (
          <button
            type="button"
            onClick={() => inputRef.current?.blur()}
            aria-label="Hide keyboard"
            className="size-11 shrink-0 rounded-full flex items-center justify-center"
            style={{ background: 'var(--app-surface)', color: 'var(--app-text)' }}
          >
            <ChevronDown className="size-5" />
          </button>
        ) : hasResponse ? (
          <button
            onClick={handleDownload}
            className="size-10 rounded-full flex items-center justify-center active:scale-90 transition-transform"
            style={{ background: 'var(--app-surface)' }}
          >
            <Download className="w-4 h-4" style={{ color: 'var(--app-text-subtle)' }} />
          </button>
        ) : (
          <div className="w-10" />
        )}
      </div>

      {/* ── Mode picker dropdown ── */}
      {showModePicker && (
        <div
          className="mx-4 mt-1 rounded-xl border overflow-hidden shrink-0 z-10"
          style={{ background: 'var(--app-surface)', borderColor: 'var(--app-border)' }}
        >
          {MODES.map(m => (
            <button
              key={m}
              onClick={() => switchMode(m)}
              className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors active:opacity-70 border-b last:border-b-0"
              style={{
                background: m === mode ? 'var(--app-tag-bg)' : 'transparent',
                borderColor: 'var(--app-divider)',
                color: m === mode ? 'var(--app-accent)' : 'var(--app-text)',
              }}
            >
              <ModeIcon mode={m} className="w-4 h-4 shrink-0" />
              <div>
                <p className="text-sm">{MODE_CONFIG[m].label}</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--app-text-muted)' }}>
                  {MODE_CONFIG[m].placeholder}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* ── Context banner (when entering from paper/session/attendee) ── */}
      {hasContext && (
        <div
          className="mx-4 mt-2 rounded-xl px-4 py-2.5 border shrink-0 flex items-start gap-3"
          style={{ background: 'var(--app-tag-bg)', borderColor: 'var(--app-tag-border)' }}
        >
          <ModeIcon mode={mode} className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="min-w-0">
            {context.paperTitle && (
              <>
                <p className="text-xs" style={{ color: 'var(--app-accent)' }}>Discussing paper</p>
                <p className="text-xs truncate" style={{ color: 'var(--app-text-subtle)' }}>
                  {context.paperTitle}
                  {context.paperAuthors ? ` · ${context.paperAuthors}` : ''}
                </p>
              </>
            )}
            {context.sessionTitle && (
              <>
                <p className="text-xs" style={{ color: 'var(--app-accent)' }}>Preparing for session</p>
                <p className="text-xs truncate" style={{ color: 'var(--app-text-subtle)' }}>
                  {context.sessionTitle}
                  {context.sessionTime ? ` · ${context.sessionTime}` : ''}
                </p>
              </>
            )}
            {context.attendeeName && (
              <>
                <p className="text-xs" style={{ color: 'var(--app-accent)' }}>Networking with</p>
                <p className="text-xs truncate" style={{ color: 'var(--app-text-subtle)' }}>
                  {context.attendeeName}
                  {context.attendeeCompany ? ` · ${context.attendeeCompany}` : ''}
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Suggestions share the message scroll area, so large text cannot push
          the composer offscreen or require hiding a feature. */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
      <AIAvailabilityNotice enabled={mode !== 'networking'} />
      {messages.length === 1 && !isLoading && !keyboardVisible && (
        <div
          className="copilot-quick-prompts pb-3 mb-4 border-b"
          style={{ borderColor: 'var(--app-border)' }}
        >
          <p className="text-xs mb-2" style={{ color: 'var(--app-text-muted)' }}>
            Quick prompts:
          </p>
          <div className="flex flex-wrap gap-2">
            {modeSuggestions.map(s => (
              <button
                key={s}
                onClick={() => sendMessage(s)}
                className="text-xs px-3 py-1.5 rounded-full border active:scale-95 transition-transform"
                style={{
                  background: 'var(--app-surface)',
                  borderColor: 'var(--app-accent-border)',
                  color: 'var(--app-text-subtle)',
                }}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Messages ── */}
        {loadingThread ? (
          <div className="flex justify-center pt-16">
            <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: 'var(--app-accent)' }} />
          </div>
        ) : (
        <div className="flex flex-col gap-4">
          {messages.map(msg => (
            <div key={msg.id} className={`flex w-full min-w-0 flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
              {/* Resolve the bubble's percentage cap against the chat width,
                  not a shrink-wrapped row that can squeeze short words. */}
              <div className={`flex w-full min-w-0 items-start gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                {msg.role === 'assistant' && (
                  <div
                    className="size-8 rounded-full flex items-center justify-center mt-0.5 shrink-0 border"
                    style={{ background: 'var(--app-tag-bg)', borderColor: 'var(--app-tag-border)' }}
                  >
                    <ModeIcon mode={mode} className="w-4 h-4" />
                  </div>
                )}
                <div
                  className={`min-w-0 max-w-[88%] rounded-2xl px-4 py-3 text-sm ${
                    msg.role === 'user' ? 'w-fit rounded-tr-sm' : 'rounded-tl-sm border'
                  }`}
                  style={
                    msg.role === 'user'
                      ? { background: 'var(--app-accent-solid)', color: '#fff' }
                      : {
                          background: 'var(--app-surface)',
                          borderColor: 'var(--app-border)',
                          color: 'var(--app-text)',
                        }
                  }
                >
                  {msg.role === 'assistant' ? (
                    <MarkdownContent content={msg.content} />
                  ) : (
                    <p className="whitespace-pre-wrap leading-relaxed text-sm" style={{ overflowWrap: 'break-word', wordBreak: 'normal' }}>{msg.content}</p>
                  )}
                </div>
              </div>

              {/* Action chips below assistant messages */}
              <ReportAIResponseButton receiptId={msg.receiptId} />
              {msg.role === 'assistant' && filterActionChips(msg.chips, isEnabled).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2 ml-10">
                  {filterActionChips(msg.chips, isEnabled).map(chip => (
                    <button
                      key={chip.action}
                      onClick={() => handleChipAction(chip)}
                      className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border active:scale-95 transition-transform"
                      style={{
                        background: 'var(--app-surface)',
                        borderColor: 'var(--app-accent-border)',
                        color: 'var(--app-accent)',
                      }}
                    >
                      <ChipIcon name={chip.icon} />
                      {chip.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Typing indicator */}
          {isLoading && (
            <div className="flex items-start gap-2">
              <div
                className="size-8 rounded-full flex items-center justify-center shrink-0 border"
                style={{ background: 'var(--app-tag-bg)', borderColor: 'var(--app-tag-border)' }}
              >
                <ModeIcon mode={mode} className="w-4 h-4" />
              </div>
              <div
                className="rounded-2xl rounded-tl-sm px-4 py-3 border"
                style={{ background: 'var(--app-surface)', borderColor: 'var(--app-border)' }}
              >
                <div className="flex flex-col gap-1.5">
                  <div className="flex gap-1.5 items-center h-4">
                    {[0, 150, 300].map(delay => (
                      <div
                        key={delay}
                        className="w-1.5 h-1.5 rounded-full animate-bounce"
                        style={{ background: 'var(--app-accent)', animationDelay: `${delay}ms` }}
                      />
                    ))}
                  </div>
                  {progressLabel && (
                    <p className="text-xs" style={{ color: 'var(--app-text-muted)' }}>
                      {progressLabel}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
        )}
      </div>

      {/* ── Input bar ── */}
      <div
        className="px-4 pt-3 border-t shrink-0"
        style={{ borderColor: 'var(--app-border)', paddingBottom: keyboardVisible ? '0.75rem' : 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}
      >
        <div
          className="flex items-center gap-3 rounded-xl px-4 border transition-colors focus-within:border-[var(--app-accent)]"
          style={{ background: 'var(--app-surface)', borderColor: 'var(--app-input-border)' }}
        >
          <ModeIcon mode={mode} className="w-4 h-4 shrink-0" />
          <textarea
            ref={inputRef}
            rows={2}
            enterKeyHint="enter"
            aria-label="Message AI Copilot"
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder={modeConfig.placeholder}
            className="min-w-0 flex-1 resize-none overflow-y-auto bg-transparent py-3 text-base leading-6 outline-none"
            style={{ color: 'var(--app-text)', fontSize: 'max(16px, 1rem)' }}
          />
          <button
            type="button"
            aria-label="Send message"
            onClick={() => sendMessage()}
            disabled={!input.trim() || isLoading}
            className="size-11 rounded-lg flex items-center justify-center disabled:opacity-40 active:scale-90 transition-transform shrink-0"
            style={{ background: 'var(--app-accent-solid)' }}
          >
            <Send className="w-4 h-4 text-white" />
          </button>
        </div>
      </div>
    </div>
  );
}
